import React, { useState, useEffect, useRef } from 'react';
import { 
  DollarSign, Plus, Edit2, Trash2, X, Save, Search, 
  RefreshCw, Calendar, TrendingUp
} from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const ExchangeRates = () => {
  // State Management
  const [loading, setLoading] = useState(true);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [filteredRates, setFilteredRates] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState(null);
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  // Form Data
  const [formData, setFormData] = useState({
    exchangerateid: null,
    fromcurrencyid: '',
    tocurrencyid: '',
    rate: '',
    effectivedate: new Date().toISOString().split('T')[0]
  });
  
  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  
  // Errors
  const [errors, setErrors] = useState({});
  
  // Refs
  const fromCurrencyRef = useRef(null);

  // Wrap loadInitialData in useCallback to avoid infinite loops
  const loadInitialDataCallback = React.useCallback(async () => {
    setLoading(true);
    try {
      await loadCurrencies();
      await loadExchangeRates();
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const filterExchangeRates = React.useCallback(() => {
    let filtered = [...exchangeRates];
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(rate => 
        rate.fromcurrencyname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rate.tocurrencyname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rate.fromcurrencysymbol?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rate.tocurrencysymbol?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rate.rate?.toString().includes(searchTerm)
      );
    }
    
    // Currency filter
    if (filterCurrency) {
      filtered = filtered.filter(rate => 
        rate.fromcurrencyid === parseInt(filterCurrency)
      );
    }
    
    // Date range filter
    if (filterDateFrom) {
      filtered = filtered.filter(rate => 
        new Date(rate.effectivedate) >= new Date(filterDateFrom)
      );
    }
    
    if (filterDateTo) {
      filtered = filtered.filter(rate => 
        new Date(rate.effectivedate) <= new Date(filterDateTo)
      );
    }
    
    setFilteredRates(filtered);
  }, [exchangeRates, searchTerm, filterCurrency, filterDateFrom, filterDateTo]);

  useEffect(() => {
    loadInitialDataCallback();
  }, [loadInitialDataCallback]);

  useEffect(() => {
    loadInitialDataCallback();
  }, [loadInitialDataCallback]);

  useEffect(() => {
    filterExchangeRates();
  }, [filterExchangeRates]);

  const loadCurrencies = async () => {
    try {
      const response = await apiClient.get('/Currencies.php', {
        params: { active: true }
      });
      if (response.data.success) {
        const currencyList = response.data.data || [];
        setCurrencies(currencyList);
        
        // Find base currency
        const base = currencyList.find(c => c.statusbasecurrency === 1);
        if (base) {
          setBaseCurrency(base);
        } else {
          alert('No base currency found. Please set a base currency in the Currencies master.');
        }
      }
    } catch (error) {
      console.error('Error loading currencies:', error);
      alert('Error loading currencies.');
    }
  };

  const loadExchangeRates = async () => {
    try {
      const response = await apiClient.get('/ExchangeRates.php');
      
      if (response.data.success) {
        setExchangeRates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading exchange rates:', error);
      alert('Error loading exchange rates.');
    }
  };

  const handleAdd = () => {
    if (!baseCurrency) {
      alert('Please set a base currency first in the Currencies master.');
      return;
    }
    
    setFormData({
      exchangerateid: null,
      fromcurrencyid: '',
      tocurrencyid: baseCurrency.currencyid,
      rate: '',
      effectivedate: new Date().toISOString().split('T')[0]
    });
    setErrors({});
    setEditMode(false);
    setShowModal(true);
    
    setTimeout(() => {
      if (fromCurrencyRef.current) {
        fromCurrencyRef.current.focus();
      }
    }, 100);
  };

  const handleEdit = (rate) => {
    setFormData({
      exchangerateid: rate.exchangerateid,
      fromcurrencyid: rate.fromcurrencyid,
      tocurrencyid: rate.tocurrencyid,
      rate: rate.rate,
      effectivedate: rate.effectivedate
    });
    setErrors({});
    setEditMode(true);
    setShowModal(true);
    
    setTimeout(() => {
      if (fromCurrencyRef.current) {
        fromCurrencyRef.current.focus();
      }
    }, 100);
  };

  const handleDelete = async (rate) => {
    // FIXED: Changed from {rate.fromcurrencyname} to ${rate.fromcurrencyname}
    if (!window.confirm(`Delete exchange rate for ${rate.fromcurrencyname}?`)) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/ExchangeRates.php', {
        data: { exchangerateid: rate.exchangerateid }
      });
      
      if (response.data.success) {
        alert('Exchange rate deleted successfully!');
        loadExchangeRates();
      } else {
        alert(response.data.message || 'Failed to delete exchange rate');
      }
    } catch (error) {
      console.error('Error deleting exchange rate:', error);
      alert('Error deleting exchange rate: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fromcurrencyid) {
      newErrors.fromcurrencyid = 'From currency is required';
    }
    
    if (formData.fromcurrencyid === formData.tocurrencyid) {
      newErrors.fromcurrencyid = 'From and To currencies must be different';
    }
    
    if (!formData.rate || parseFloat(formData.rate) <= 0) {
      newErrors.rate = 'Valid exchange rate is required';
    }
    
    if (!formData.effectivedate) {
      newErrors.effectivedate = 'Effective date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      const payload = {
        fromcurrencyid: parseInt(formData.fromcurrencyid),
        tocurrencyid: parseInt(formData.tocurrencyid),
        rate: parseFloat(formData.rate),
        effectivedate: formData.effectivedate
      };
      
      let response;
      if (editMode) {
        payload.exchangerateid = formData.exchangerateid;
        response = await apiClient.put('/ExchangeRates.php', payload);
      } else {
        response = await apiClient.post('/ExchangeRates.php', payload);
      }
      
      if (response.data.success) {
        // FIXED: Changed from {editMode ? 'updated' : 'created'} to ${editMode ? 'updated' : 'created'}
        alert(`Exchange rate ${editMode ? 'updated' : 'created'} successfully!`);
        setShowModal(false);
        loadExchangeRates();
      } else {
        // FIXED: Changed from {editMode ? 'update' : 'create'} to ${editMode ? 'update' : 'create'}
        alert(response.data.message || `Failed to ${editMode ? 'update' : 'create'} exchange rate`);
      }
    } catch (error) {
      console.error('Error saving exchange rate:', error);
      const errorMessage = error.response?.data?.message || error.message;
      
      // Check for duplicate error
      if (errorMessage.includes('already exists')) {
        setErrors(prev => ({
          ...prev,
          effectivedate: 'Exchange rate already exists for this date'
        }));
      } else {
        alert('Error saving exchange rate: ' + errorMessage);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadExchangeRates();
    alert('Exchange rates refreshed successfully!');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterCurrency('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const getCurrencyDisplay = (currencyId) => {
    const currency = currencies.find(c => c.currencyid === parseInt(currencyId));
    // FIXED: Changed from {currency.currencyname} ({currency.symbol}) to ${currency.currencyname} (${currency.symbol})
    return currency ? `${currency.currencyname} (${currency.symbol})` : '';
  };

  if (loading) {
    return (
      <div className="master-container">
        <div className="loading-state">Loading Exchange Rates...</div>
      </div>
    );
  }

  return (
    <div className="master-container">
      {/* Header */}
      <div className="master-header">
        <div className="header-left">
          <h2>
            <DollarSign size={32} style={{ display: 'inline', marginRight: '10px', verticalAlign: 'middle' }} />
            Exchange Rates Management
          </h2>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleAdd}>
            <Plus size={18} />
            Add Exchange Rate
          </button>
          <button className="btn-secondary" onClick={handleRefresh}>
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {/* Base Currency Info */}
      {baseCurrency && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <TrendingUp size={20} style={{ color: '#166534' }} />
          <span style={{ color: '#166534', fontWeight: '500' }}>
            Base Currency: {baseCurrency.currencyname} ({baseCurrency.symbol})
          </span>
        </div>
      )}

      {/* Search and Filters */}
      <div className="search-filter-container">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by currency name, symbol, or rate..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filter-group">
          <select
            value={filterCurrency}
            onChange={(e) => setFilterCurrency(e.target.value)}
            className="filter-select"
          >
            <option value="">All Currencies</option>
            {currencies
              .filter(c => c.currencyid !== baseCurrency?.currencyid)
              .map(currency => (
                <option key={currency.currencyid} value={currency.currencyid}>
                  {currency.currencyname} ({currency.symbol})
                </option>
              ))}
          </select>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Calendar size={16} style={{ color: '#6b7280' }} />
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="filter-date"
              placeholder="From Date"
            />
            <span style={{ color: '#6b7280' }}>to</span>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="filter-date"
              placeholder="To Date"
            />
          </div>

          {(searchTerm || filterCurrency || filterDateFrom || filterDateTo) && (
            <button className="btn-secondary" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-label">Total Rates</div>
          <div className="stat-value">{exchangeRates.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Filtered Results</div>
          <div className="stat-value">{filteredRates.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Currencies</div>
          <div className="stat-value">{currencies.filter(c => c.isactive === 1).length}</div>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>From Currency</th>
              <th>To Currency</th>
              <th>Exchange Rate</th>
              <th>Effective Date</th>
              <th>Last Updated</th>
              <th className="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRates.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  {exchangeRates.length === 0 ? (
                    <>
                      <DollarSign size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                      <div>No exchange rates found. Click "Add Exchange Rate" to create one.</div>
                    </>
                  ) : (
                    <>
                      <Search size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                      <div>No exchange rates match your filters.</div>
                      <button className="btn-link" onClick={clearFilters} style={{ marginTop: '8px' }}>
                        Clear filters
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              filteredRates.map((rate) => (
                <tr key={rate.exchangerateid}>
                  <td>
                    <div className="currency-cell">
                      <span className="currency-badge">
                        {rate.fromcurrencyname}
                      </span>
                      <span className="currency-symbol">{rate.fromcurrencysymbol}</span>
                    </div>
                  </td>
                  <td>
                    <div className="currency-cell">
                      <span className="currency-badge">
                        {rate.tocurrencyname}
                      </span>
                      <span className="currency-symbol">{rate.tocurrencysymbol}</span>
                    </div>
                  </td>
                  <td>
                    <span className="rate-value">
                      {parseFloat(rate.rate).toFixed(6)}
                    </span>
                  </td>
                  <td>
                    {new Date(rate.effectivedate).toLocaleDateString('en-GB')}
                  </td>
                  <td>
                    {rate.updatedat ? new Date(rate.updatedat).toLocaleDateString('en-GB') : '-'}
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(rate)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(rate)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <DollarSign size={24} />
                {editMode ? 'Edit Exchange Rate' : 'Add New Exchange Rate'}
              </h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="fromcurrencyid">
                    From Currency <span className="required">*</span>
                  </label>
                  <select
                    ref={fromCurrencyRef}
                    id="fromcurrencyid"
                    value={formData.fromcurrencyid}
                    onChange={(e) => handleInputChange('fromcurrencyid', e.target.value)}
                    className={errors.fromcurrencyid ? 'error' : ''}
                  >
                    <option value="">-- Select Currency --</option>
                    {currencies
                      .filter(c => c.currencyid !== baseCurrency?.currencyid && c.isactive === 1)
                      .map(currency => (
                        <option key={currency.currencyid} value={currency.currencyid}>
                          {currency.currencyname} ({currency.symbol})
                        </option>
                      ))}
                  </select>
                  {errors.fromcurrencyid && (
                    <span className="error-message">{errors.fromcurrencyid}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="tocurrencyid">
                    To Currency (Base) <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="tocurrencyid"
                    value={getCurrencyDisplay(formData.tocurrencyid)}
                    readOnly
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Base currency is set in Currencies master
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="rate">
                    Exchange Rate <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    id="rate"
                    step="0.000001"
                    value={formData.rate}
                    onChange={(e) => handleInputChange('rate', e.target.value)}
                    className={errors.rate ? 'error' : ''}
                    placeholder="e.g., 1.085000"
                  />
                  {errors.rate && (
                    <span className="error-message">{errors.rate}</span>
                  )}
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Rate must be greater than 0 (6 decimal places)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="effectivedate">
                    Effective Date <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    id="effectivedate"
                    value={formData.effectivedate}
                    onChange={(e) => handleInputChange('effectivedate', e.target.value)}
                    className={errors.effectivedate ? 'error' : ''}
                  />
                  {errors.effectivedate && (
                    <span className="error-message">{errors.effectivedate}</span>
                  )}
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Date from which this rate is effective
                  </small>
                </div>

                {/* Calculation Helper */}
                {formData.fromcurrencyid && formData.rate && (
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '6px'
                  }}>
                    <strong>Rate Preview:</strong>
                    <div style={{ marginTop: '8px', fontSize: '14px' }}>
                      1 {currencies.find(c => c.currencyid === parseInt(formData.fromcurrencyid))?.symbol} = {parseFloat(formData.rate || 0).toFixed(6)} {baseCurrency?.symbol}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  <Save size={18} />
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .currency-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .currency-badge {
          padding: 4px 10px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
        }

        .currency-symbol {
          color: #6b7280;
          font-size: 12px;
          font-weight: 600;
        }

        .rate-value {
          font-weight: 600;
          color: #1e40af;
          font-size: 15px;
        }

        .filter-date {
          padding: 6px 10px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
        }

        .stats-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 20px;
        }

        .stat-card {
          padding: 16px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .stat-label {
          font-size: 13px;
          color: #6b7280;
          margin-bottom: 8px;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>
    </div>
  );
};

export default ExchangeRates;